/* eslint react/display-name: 0, react/no-multi-comp: 0 */
import React, { Component, Fragment, ReactNode, ReactElement } from 'react'
import PropTypes from 'prop-types'
// Until we update our tsconfig target we don't have optional chaining
import get from 'lodash/get'
import algoliasearch, { SearchClient } from 'algoliasearch'
import { distancePerState } from '../../helpers/distance-per-state'
import analyticsUtils from '../../helpers/analytics-utils'
import { getNowInCentreInTimeFormat } from '../../helpers/date-helpers'
import { VIEW_OPTION_MEMBERS_ONLY } from '../../constants'
import { Centre } from '../../../types/contentful-v1'
import SearchResult from './search-result'
import { CentreType } from './types'
import SearchView from './search-view'
import style from './index.scss'

const HEADINGS = {
  products: 'Products',
  services: 'Centre services',
  topStores: 'Top Stores',
  events: 'Events',
  offers: 'Offers',
  stories: 'Story',
  news: 'News',
  centres: 'Centres',
  nearbyStores: 'Other nearby stores',
  otherStores: 'Other stores near',
}

type MarketplaceIndex = 'products_au'

type ContentfulIndex =
  | 'retailer'
  | 'store'
  | 'site'
  | 'service'
  | 'offer'
  | 'event'
  | 'news'
  | 'story'
  | 'centre'

type Index = MarketplaceIndex | ContentfulIndex

// Expected shape of Algolia hits for various indexes:

interface AlgoliaHit {
  objectID: string
}

interface ProductHit extends AlgoliaHit {
  name: string
  shop: { id: string; name: string }
  description?: string
  store?: string
  image?: string
  variant?: {
    price?: { effectivePrice?: number }
  }
  variantGroupCode?: string
}

interface ContentfulHit extends AlgoliaHit {
  slug: string
  img: string
  title: string
}

interface PageHit extends ContentfulHit {
  href: string
}

interface StoreHit extends ContentfulHit {
  centreSlug: string
  centreName: string
}

interface OfferHit extends ContentfulHit {
  storeTitles: string[]
}

interface PostHit extends ContentfulHit {
  shortDescription: string
}

type Hit<T extends Index> = T extends 'products_au'
  ? ProductHit
  : T extends 'store'
  ? StoreHit
  : T extends 'site' | 'centre'
  ? PageHit
  : T extends 'offer'
  ? OfferHit
  : T extends 'event' | 'story' | 'news'
  ? PostHit
  : ContentfulHit

// Placeholder until the relevant type from algoliasearch can be imported:
interface SearchParams {
  filters?: string
  aroundLatLng?: string
  facetFilters?: string
  aroundRadius?: number
}

interface Query<T extends Index> {
  indexName: T
  query: string
  params?: SearchParams
}

interface Section<T extends Index> extends Query<T> {
  heading: string
  render: React.FC<Hit<T>>
}

type ContentfulSections = Section<ContentfulIndex>[]
type MarketplaceSections = Section<MarketplaceIndex>[]
type AllSections = Section<Index>[]

interface RawResults {
  hits: AlgoliaHit[]
}

interface RenderedSectionResults {
  heading: string
  hits: ReactNode[]
}

export interface AlgoliaApiKeys {
  contentApplicationId?: string
  contentApiKey?: string
  marketplaceApplicationId?: string
  marketplaceApiKey?: string
}

interface SearchProps {
  country: string
  algoliaApiKeys: AlgoliaApiKeys
  children(showSearch: () => void): ReactElement
  centre?: Centre
  isEvolved?: boolean
}

const ESCAPE_CODE = 'Escape'

const hrefForStore = (centreSlug: string, store: StoreHit): string =>
  `/${centreSlug}/store/${store.objectID}/${store.slug}`

const descriptionForProduct = (hit: ProductHit): string => {
  const price = get(hit, ['variant', 'price', 'effectivePrice'])
  const priceDecimals = 2
  const prefix = Number.isFinite(price)
    ? `$${price.toFixed(priceDecimals)}`
    : ''
  const suffix = get(hit, ['shop', 'name'], '')
  const infix = prefix.length > 0 && suffix.length > 0 ? ' from ' : ''
  return `${prefix}${infix}${suffix}`
}

// The queries we run whenever text changes, and how the results turn into dom.
// Everything here is passed to algolia except for the render method.

const centreWithDateAndViewOptionFilter = (
  centreID: string,
  viewOptionToExclude: string,
  getOffsetlessTimeInCentre: () => number
): SearchParams => ({
  filters: `centres:${centreID} AND (NOT viewOption:"${viewOptionToExclude}") AND (showAt<${getOffsetlessTimeInCentre()} AND hideAt>${getOffsetlessTimeInCentre()})`,
})

const singleCentreWithDateFilter = (
  centreID: string,
  getOffsetlessTimeInCentre: () => number
): SearchParams => ({
  filters: `centre:${centreID} AND (showAt<${getOffsetlessTimeInCentre()} AND hideAt>${getOffsetlessTimeInCentre()})`,
})

const retailerFilter = (country: string): SearchParams => ({
  filters: `hasMarketplaceShopId:true AND country:"${country}"`,
})

type OnResultClick = (heading: string, hit: ContentfulHit) => void

interface SectionContext {
  country: string
  centre?: Centre
  getOffsetlessTimeInCentre?: () => number
}

type GetSectionContextual<T extends Index> = (
  query: string,
  onResultClick: OnResultClick,
  sectionContext: SectionContext
) => Section<T> | null

const inCentreContext = <T extends Index>(
  getSectionWithCentre: (
    query: string,
    onResultClick: OnResultClick,
    centre: Centre,
    getOffsetlessTimeInCentre: () => number
  ) => Section<T> | null
): GetSectionContextual<T> => (
  query: string,
  onResultClick: OnResultClick,
  sectionContext: SectionContext
) =>
  sectionContext.centre && sectionContext.getOffsetlessTimeInCentre
    ? getSectionWithCentre(
        query,
        onResultClick,
        sectionContext.centre,
        sectionContext.getOffsetlessTimeInCentre
      )
    : null

const getRetailerSection = (
  query: string,
  onResultClick: OnResultClick,
  { country }: { country: string }
): Section<'retailer'> => ({
  heading: HEADINGS.topStores,
  indexName: 'retailer',
  query,
  params: retailerFilter(country),
  render: (hit: Hit<'retailer'>) => (
    <SearchResult
      key={hit.objectID}
      href={`/store/${hit.objectID}/${hit.slug}`}
      imageUrl={hit.img}
      highlightField="title"
      hit={hit}
      description="Shop online"
      className={style.storeRow}
      onClick={() => onResultClick(HEADINGS.topStores, hit)}
    />
  ),
})

const getStoreSection = inCentreContext(
  (
    query: string,
    onResultClick: OnResultClick,
    centre: Centre,
    getOffsetlessTimeInCentre: () => number
  ): Section<'store'> => ({
    heading: HEADINGS.topStores,
    indexName: 'store',
    query,
    params: singleCentreWithDateFilter(
      centre.sys.id,
      getOffsetlessTimeInCentre
    ),
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={hrefForStore(centre.slug, hit)}
        imageUrl={hit.img}
        highlightField="titleWithOptionalStatus"
        hit={hit}
        className={style.storeRow}
        onClick={() => onResultClick(HEADINGS.topStores, hit)}
      />
    ),
  })
)

const getSiteSection = inCentreContext(
  (
    query: string,
    onResultClick: OnResultClick,
    centre: Centre,
    _getOffsetlessTimeInCentre: () => number
  ): Section<'site'> => ({
    heading: HEADINGS.services,
    indexName: 'site',
    query,
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={(hit.href || '').replace('CENTRE', centre.slug)}
        imageUrl={hit.img}
        highlightField="text"
        hit={hit}
        onClick={() => onResultClick(HEADINGS.services, hit)}
      />
    ),
  })
)

const getServiceSection = inCentreContext(
  (
    query: string,
    onResultClick: OnResultClick,
    centre: Centre,
    getOffsetlessTimeInCentre: () => number
  ): Section<'service'> => ({
    heading: HEADINGS.services,
    indexName: 'service',
    query,
    params: singleCentreWithDateFilter(
      centre.sys.id,
      getOffsetlessTimeInCentre
    ),
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={`/${centre.slug}/service/${hit.objectID}/${hit.slug}`}
        imageUrl={hit.img}
        highlightField="title"
        hit={hit}
        onClick={() => onResultClick(HEADINGS.services, hit)}
      />
    ),
  })
)

const getOfferSection = inCentreContext(
  (
    query: string,
    onResultClick: OnResultClick,
    centre: Centre,
    getOffsetlessTimeInCentre: () => number
  ): Section<'offer'> => ({
    heading: HEADINGS.offers,
    indexName: 'offer',
    query,
    params: centreWithDateAndViewOptionFilter(
      centre.sys.id,
      VIEW_OPTION_MEMBERS_ONLY,
      getOffsetlessTimeInCentre
    ),
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={`/${centre.slug}/offer/${hit.objectID}/${hit.slug}`}
        imageUrl={hit.img}
        highlightField="title"
        hit={hit}
        description={hit.storeTitles.join(', ')}
        onClick={() => onResultClick(HEADINGS.offers, hit)}
      />
    ),
  })
)

const getEventSection = inCentreContext(
  (
    query: string,
    onResultClick: OnResultClick,
    centre: Centre,
    getOffsetlessTimeInCentre: () => number
  ): Section<'event'> => ({
    heading: HEADINGS.events,
    indexName: 'event',
    query,
    params: centreWithDateAndViewOptionFilter(
      centre.sys.id,
      VIEW_OPTION_MEMBERS_ONLY,
      getOffsetlessTimeInCentre
    ),
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={`/${centre.slug}/event/${hit.objectID}/${hit.slug}`}
        imageUrl={hit.img}
        highlightField="title"
        hit={hit}
        description={hit.shortDescription}
        onClick={() => onResultClick(HEADINGS.events, hit)}
      />
    ),
  })
)

const getNewsSection = inCentreContext(
  (
    query: string,
    onResultClick: OnResultClick,
    centre: Centre,
    getOffsetlessTimeInCentre: () => number
  ): Section<'news'> => ({
    heading: HEADINGS.news,
    indexName: 'news',
    query,
    params: centreWithDateAndViewOptionFilter(
      centre.sys.id,
      VIEW_OPTION_MEMBERS_ONLY,
      getOffsetlessTimeInCentre
    ),
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={`/${centre.slug}/news/${hit.objectID}/${hit.slug}`}
        imageUrl={hit.img}
        highlightField="title"
        hit={hit}
        description={hit.shortDescription}
        onClick={() => onResultClick(HEADINGS.news, hit)}
      />
    ),
  })
)

const getStorySection = inCentreContext(
  (
    query: string,
    onResultClick: OnResultClick,
    centre: Centre,
    getOffsetlessTimeInCentre: () => number
  ): Section<'story'> => ({
    heading: HEADINGS.stories,
    indexName: 'story',
    query,
    params: centreWithDateAndViewOptionFilter(
      centre.sys.id,
      VIEW_OPTION_MEMBERS_ONLY,
      getOffsetlessTimeInCentre
    ),
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={`/${centre.slug}/story/${hit.objectID}/${hit.slug}`}
        imageUrl={hit.img}
        highlightField="title"
        hit={hit}
        description={hit.shortDescription}
        onClick={() => onResultClick(HEADINGS.stories, hit)}
      />
    ),
  })
)

const getCentreSection = (
  query: string,
  onResultClick: OnResultClick,
  { country }: { country: string }
): Section<'centre'> => ({
  heading: HEADINGS.centres,
  indexName: 'centre',
  query,
  params: {
    filters: `country:"${country}"`,
  },
  render: hit => (
    <SearchResult
      key={hit.objectID}
      href={hit.href}
      imageUrl={hit.img}
      highlightField="title"
      hit={hit}
      onClick={() => onResultClick(HEADINGS.centres, hit)}
    />
  ),
})

const getOtherStoreSection = inCentreContext(
  (
    query: string,
    onResultClick: OnResultClick,
    centre: Centre,
    _getOffsetlessTimeInCentre: () => number
  ): Section<'store'> => ({
    heading: `${HEADINGS.otherStores} ${centre.title}`,
    indexName: 'store',
    query,
    params: {
      aroundLatLng: `${centre.latitude}, ${centre.longitude}`,
      facetFilters: `centre:-${centre.sys.id}`,
      aroundRadius: distancePerState(centre.state), // in meters
    },
    render: hit => (
      <SearchResult
        noImage
        key={hit.objectID}
        href={hrefForStore(hit.centreSlug, hit)}
        highlightField="title"
        hit={hit}
        description={hit.centreName}
        className={style.otherStoreRow}
        onClick={() =>
          onResultClick(`${HEADINGS.otherStores} ${centre.title}`, hit)
        }
      />
    ),
  })
)

const isSection = (
  maybeSection: Section<Index> | null
): maybeSection is Section<Index> => maybeSection !== null

const getContentfulSections = (
  query: string,
  onResultClick: OnResultClick,
  country: string,
  centre?: Centre,
  getOffsetlessTimeInCentre?: () => number,
  isEvolved?: boolean
): ContentfulSections =>
  [
    isEvolved ? getRetailerSection : () => null,
    getStoreSection,
    getSiteSection,
    getServiceSection,
    getOfferSection,
    getEventSection,
    getNewsSection,
    getStorySection,
    getCentreSection,
    getOtherStoreSection,
  ]
    .map(get =>
      get(query, onResultClick, { country, centre, getOffsetlessTimeInCentre })
    )
    .filter(isSection) as ContentfulSections // I have No Idea why this cast is needed

const getMarketplaceSections = (
  query: string,
  onResultClick: (heading: string, hit: ProductHit) => void,
  country: string,
  isEvolved?: boolean
): MarketplaceSections =>
  isEvolved && country === 'Australia'
    ? [
        {
          heading: HEADINGS.products,
          indexName: 'products_au',
          query,
          render: hit => (
            <SearchResult
              imageUrl={hit.image}
              key={hit.objectID}
              href={`/shop/product/${hit.shop.id}/${hit.variantGroupCode}`}
              highlightField="name"
              hit={hit}
              description={descriptionForProduct(hit)}
              onClick={() => onResultClick(HEADINGS.products, hit)}
            />
          ),
        },
      ]
    : []

// Results:
// 1. Get rid of results with no hits
// 2. Merge results with the same header
// 3. Return the heading and render method
export const buildResults = (
  results: RawResults[],
  sections: AllSections
): RenderedSectionResults[] =>
  results.reduce(
    (
      acc: RenderedSectionResults[],
      { hits }: { hits: AlgoliaHit[] },
      i: number
    ) => {
      if (hits.length > 0) {
        const { render, heading } = sections[i]
        const matchingIndex = acc.findIndex(res => res.heading === heading)
        const newHits = hits.map(render as React.FC<AlgoliaHit>)

        if (matchingIndex >= 0) {
          acc[matchingIndex].hits = [...acc[matchingIndex].hits, ...newHits]
        } else {
          acc.push({ heading, hits: newHits })
        }
      }
      return acc
    },
    []
  )

// This is sort of a controller component which runs search queries against
// algolia and re-renders.
export class Search extends Component<SearchProps> {
  static propTypes = {
    children: PropTypes.node,
    centre: CentreType,
    isEvolved: PropTypes.bool,
  }
  constructor(props: SearchProps) {
    super(props)
    this.contentfulClient = null
    this.marketplaceClient = null
    this.getOffsetlessTimeInCentre = () => 0 // in practice this is always set on query text change
    const { algoliaApiKeys } = this.props
    if (!algoliaApiKeys.contentApiKey || !algoliaApiKeys.contentApplicationId) {
      return
    }
    this.contentfulClient = algoliasearch(
      // It's fine to put these in the client.
      // https://www.algolia.com/doc/guides/security/api-keys/?language=php#search-only-api-key
      algoliaApiKeys.contentApplicationId,
      algoliaApiKeys.contentApiKey
    )
    if (
      algoliaApiKeys.marketplaceApiKey &&
      algoliaApiKeys.marketplaceApplicationId &&
      props.isEvolved
    ) {
      this.marketplaceClient = algoliasearch(
        algoliaApiKeys.marketplaceApplicationId,
        algoliaApiKeys.marketplaceApiKey
      )
    }
  }

  state = {
    nextClock: 1,
    visible: false,
    clock: 0,
    results: [], // List of sections. Each section has {heading, hits: [...]}.
    queryText: '',
  }

  private contentfulClient: SearchClient | null
  private marketplaceClient: SearchClient | null
  private getOffsetlessTimeInCentre: () => number

  _handleKeyDown = (event: KeyboardEvent): void => {
    switch (event.code) {
      case ESCAPE_CODE:
        this.hideSearch()
        break
      default:
        break
    }
  }

  setResults(clock: number, results: RenderedSectionResults[]): void {
    // The clock ensures we don't have race conditions from earlier search
    // results coming back after later ones
    if (clock > this.state.clock) {
      this.setState({
        clock,
        results,
      })
    }
  }

  onQueryChange = async (queryText: string): Promise<void> => {
    this.setState({ queryText })
    // Used to make sure we always render the last
    const clock = this.state.nextClock
    this.setState({ nextClock: this.state.nextClock + 1 })

    if (!this.contentfulClient || queryText.trim() === '') {
      // Show no results if the query text is deleted or there is no client.
      this.setResults(clock, [])
      return
    }

    const onResultClick = (
      heading: string,
      hit: ContentfulHit | ProductHit
    ): void => {
      analyticsUtils.gaTagTracking({
        event: analyticsUtils.events.SEARCH,
        eventCategory: analyticsUtils.categories.GLOBAL_SEARCH,
        eventAction: analyticsUtils.actions.SEARCH_RESULT_ClICKED,
        eventLabel: queryText,
        globalResultClicked: `${heading}/${
          (hit as ContentfulHit).title || (hit as ProductHit).name
        }`,
      })
    }
    if (this.props.centre) {
      this.getOffsetlessTimeInCentre = getNowInCentreInTimeFormat(
        this.props.centre.timeZone
      ) as () => number // TS won't infer this type for some reason
    }
    const contentfulSections = getContentfulSections(
      queryText,
      onResultClick,
      this.props.country,
      this.props.centre,
      this.getOffsetlessTimeInCentre,
      this.props.isEvolved
    )
    const marketplaceSections = getMarketplaceSections(
      queryText,
      onResultClick,
      this.props.country,
      this.props.isEvolved
    )
    const makeQueries = (sections: Section<Index>[]): Query<Index>[] =>
      sections.map(section => {
        const { params, query, indexName } = section
        return { params, query, indexName }
      })
    const searches = [
      this.contentfulClient.search(makeQueries(contentfulSections)),
    ]
    if (this.marketplaceClient && marketplaceSections.length > 0) {
      searches.push(
        this.marketplaceClient.search(makeQueries(marketplaceSections))
      )
    }
    const [contentfulContent, marketplaceContent] = await Promise.all(searches)
    const allSections = [...marketplaceSections, ...contentfulSections]
    const results = buildResults(
      [
        ...(marketplaceContent ? marketplaceContent.results : []),
        ...contentfulContent.results,
      ],
      allSections
    )
    this.setResults(clock, results)
    const numHits = results.reduce(
      (acc: number, { hits }: { hits: ReactNode[] }) => acc + hits.length,
      0
    )
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.SEARCH,
      eventCategory: analyticsUtils.categories.GLOBAL_SEARCH,
      eventAction: analyticsUtils.actions.SEARCH_SHOWN,
      eventLabel: this.state.queryText,
      noReturnedResults: numHits,
    })
  }

  hideSearch = (): void => {
    document.removeEventListener('keydown', this._handleKeyDown)
    window.scroll(0, 0)
    this.setState({ visible: false })
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.SEARCH,
      eventCategory: analyticsUtils.categories.GLOBAL_SEARCH,
      eventAction: analyticsUtils.actions.CLICK_TO_CLOSE_SEARCH,
      eventLabel: this.state.queryText,
    })
  }

  showSearch = (): void => {
    document.addEventListener('keydown', this._handleKeyDown)
    this.setState({ visible: true })
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.SEARCH,
      eventCategory: analyticsUtils.categories.GLOBAL_SEARCH,
      eventAction: analyticsUtils.actions.CLICK_TO_START_SEARCH,
    })
  }

  onQueryKeyDown = (e: KeyboardEvent): void => {
    if (e.code === ESCAPE_CODE) {
      // Escape cancels the search modal.
      this.hideSearch()
    }
  }

  onClickClear = (): void => {
    const clock = this.state.nextClock
    this.setState({
      queryText: '',
      nextClock: this.state.nextClock + 1,
      clock,
      results: [],
    })
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.SEARCH,
      eventCategory: analyticsUtils.categories.GLOBAL_SEARCH,
      eventAction: analyticsUtils.actions.CLICK_TO_CLEAR_SEARCH_KEYWORD,
      eventLabel: this.state.queryText,
    })
  }

  render(): ReactNode {
    const { algoliaApiKeys } = this.props
    if (!algoliaApiKeys.contentApplicationId || !algoliaApiKeys.contentApiKey) {
      return null
    }

    const { isEvolved } = this.props

    return (
      <Fragment>
        <SearchView
          results={this.state.results}
          queryText={this.state.queryText}
          isVisible={this.state.visible}
          isEvolved={isEvolved}
          onQueryChange={this.onQueryChange}
          onQueryKeyDown={this.onQueryKeyDown}
          onClickClose={this.hideSearch}
          onClickClear={this.onClickClear}
        />
        {this.props.children(this.showSearch)}
      </Fragment>
    )
  }
}

export default Search
